import { useState, useEffect, useRef } from "react";
import { Bell, X, Check, CheckCheck, FileText, Package, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationBell = ({ token, lightMode = false }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const prevCountRef = useRef(0);
  const pushPermAsked = useRef(false);
  const navigate = useNavigate();

  // Request browser notification permission on mount
  useEffect(() => {
    audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleAdclsHWqX8iEnqoztt1JQkigaCy2p1UNhxUl8LSqWgvI1eGxs+ogk4qRIeRxO2lflQ2PJfU45SJ");
    if ("Notification" in window && Notification.permission === "default" && !pushPermAsked.current) {
      pushPermAsked.current = true;
      setTimeout(() => Notification.requestPermission(), 3000);
    }
  }, []);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newCount = response.data.count;
        
        if (newCount > prevCountRef.current && prevCountRef.current !== 0) {
          audioRef.current?.play().catch(() => {});
          // Show browser push notification
          showBrowserNotification(newCount - prevCountRef.current);
        }
        prevCountRef.current = newCount;
        setUnreadCount(newCount);
      } catch (error) {
        console.error("Failed to fetch notification count:", error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, [token]);

  const showBrowserNotification = async (newNotifCount) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    try {
      const res = await axios.get(`${API}/notifications?limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const latest = res.data?.[0];
      if (latest) {
        new Notification(latest.title || "Happy Holidays DMC", {
          body: latest.message || `You have ${newNotifCount} new notification(s)`,
          icon: "https://customer-assets.emergentagent.com/job_be106047-7bc1-4755-b814-a58a16500b58/artifacts/p2la4lzy_ChatGPT%20Image%20Dec%2028%2C%202025%2C%2009_16_43%20PM.png",
          tag: "hh-dmc-notif"
        });
      }
    } catch {
      new Notification("Happy Holidays DMC", {
        body: `You have ${newNotifCount} new notification(s)`,
        tag: "hh-dmc-notif"
      });
    }
  };

  const fetchNotifications = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove notification from list after reading (auto-delete)
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Clear all notifications (auto-delete)
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (notification.reference_type === "quote" && notification.reference_id) {
      navigate(window.location.pathname.startsWith("/agent") ? "/agent/quotes" : "/admin/quotes");
    } else if (notification.reference_type === "booking" && notification.reference_id) {
      navigate(window.location.pathname.startsWith("/agent") ? "/agent/bookings" : "/admin/bookings");
    } else if (notification.reference_type === "agent") {
      navigate("/admin/settings");
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "quote_created":
      case "quote_replied":
      case "quote_updated":
        return <FileText className="w-4 h-4 text-emerald-500" />;
      case "booking_confirmed":
      case "booking_created":
        return <Package className="w-4 h-4 text-amber-500" />;
      case "agent_registered":
        return <UserPlus className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return "Just now";
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className={`relative p-2 rounded-lg transition-colors ${
          lightMode 
            ? "text-gray-500 hover:text-gray-700 hover:bg-gray-100" 
            : "text-slate-300 hover:text-white hover:bg-slate-800"
        }`}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute right-0 mt-2 w-80 rounded-xl shadow-xl border z-50 overflow-hidden ${
          lightMode 
            ? "bg-white border-gray-200" 
            : "bg-slate-800 border-slate-700"
        }`}>
          {/* Header */}
          <div className={`px-4 py-3 border-b flex items-center justify-between ${
            lightMode ? "border-gray-100 bg-gray-50" : "border-slate-700"
          }`}>
            <h3 className={`font-semibold ${lightMode ? "text-gray-800" : "text-white"}`}>Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 font-medium"
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className={`px-4 py-8 text-center ${lightMode ? "text-gray-400" : "text-slate-400"}`}>
                <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-2" />
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className={`px-4 py-8 text-center ${lightMode ? "text-gray-400" : "text-slate-400"}`}>
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 border-b cursor-pointer transition-colors ${
                    lightMode
                      ? notification.is_read 
                        ? "bg-white hover:bg-gray-50 border-gray-50" 
                        : "bg-emerald-50/50 hover:bg-emerald-50 border-gray-100"
                      : notification.is_read 
                        ? "bg-slate-800 hover:bg-slate-750 border-slate-700/50" 
                        : "bg-slate-700/50 hover:bg-slate-700 border-slate-700/50"
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${
                        lightMode
                          ? notification.is_read ? "text-gray-600" : "text-gray-800 font-medium"
                          : notification.is_read ? "text-slate-300" : "text-white font-medium"
                      }`}>
                        {notification.title}
                      </p>
                      <p className={`text-xs mt-1 line-clamp-2 ${lightMode ? "text-gray-500" : "text-slate-400"}`}>
                        {notification.message}
                      </p>
                      <p className={`text-xs mt-1 ${lightMode ? "text-gray-400" : "text-slate-500"}`}>
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className={`px-4 py-2 border-t text-center ${
              lightMode ? "border-gray-100 bg-gray-50" : "border-slate-700"
            }`}>
              <button 
                onClick={() => setIsOpen(false)}
                className={`text-xs ${lightMode ? "text-gray-500 hover:text-gray-700" : "text-slate-400 hover:text-white"}`}
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
